/**
 * A disclosure that shows partial contents and, if the contents are taller
 * than some max height, an optional fade and button that toggles whether the
 * full contents are shown or clipped.
 * */ import React, { useRef, useState, useCallback } from 'react';
import classnames from 'classnames';
import { useResizeObserver } from '@wix/da-hooks/pkg/useResizeObserver';
import { useThrottle } from '@wix/da-hooks/pkg/useThrottle';
import s from './CollapsibleBlock.scss';
const RESIZE_THROTTLE_DELAY = 300;
const RESIZE_THROTTLE_OPTIONS = {
    trailing: true
};
const CollapsibleBlock = (props)=>{
    const { buttonTextCollapsed, buttonTextExpanded, maxHeight, className, collapsedClassName, shutterBlockClassName, buttonClassName, showFade, fadeClassName, renderToggleButton, onCollapsed, onExpanded, children } = props;
    const contentBlockRef = useRef(null);
    const [collapsed, setCollapsed] = useState(true);
    // will default to taller in SSR
    const [contentHeight, setContentHeight] = useState(maxHeight);
    const isTallerThanMaxHeight = contentHeight > maxHeight;
    // we only show 'as collapsed' if there's enough content to spill over
    const showAsCollapsed = isTallerThanMaxHeight && collapsed;
    // re-calculate if the size of the contents changes
    // note that ResizeObserver is more of a proxy for DOMSubtreeModified,
    // so it's possible we receive resize events to the same size
    const handleResize = useThrottle(()=>{
        const newHeight = contentBlockRef.current.clientHeight;
        if (newHeight !== contentHeight) {
            setContentHeight(newHeight);
            setCollapsed(newHeight > maxHeight);
        }
    }, [
        maxHeight,
        contentHeight
    ], RESIZE_THROTTLE_DELAY, RESIZE_THROTTLE_OPTIONS);
    useResizeObserver(contentBlockRef, handleResize);
    const toggleButtonClick = useCallback(()=>{
        setCollapsed(!collapsed);
        if (!collapsed) {
            onCollapsed && onCollapsed();
        } else {
            onExpanded && onExpanded();
        }
    }, [
        collapsed,
        onCollapsed,
        onExpanded
    ]);
    const classes = classnames(showAsCollapsed && collapsedClassName, className);
    return /*#__PURE__*/ React.createElement("div", {
        className: classes
    }, /*#__PURE__*/ React.createElement("div", {
        className: s['fade-container']
    }, /*#__PURE__*/ React.createElement("div", {
        className: classnames(s['shutter-block'], shutterBlockClassName),
        style: {
            maxHeight: collapsed ? maxHeight : contentHeight
        }
    }, /*#__PURE__*/ React.createElement("div", {
        ref: contentBlockRef
    }, children)), showAsCollapsed && showFade && /*#__PURE__*/ React.createElement("div", {
        className: classnames(s['fade'], fadeClassName)
    })), isTallerThanMaxHeight && (renderToggleButton ? renderToggleButton({
        collapsed,
        onClick: toggleButtonClick
    }) : /*#__PURE__*/ React.createElement("button", {
        className: classnames(buttonClassName, s['toggle-button']),
        onClick: toggleButtonClick
    }, collapsed ? buttonTextCollapsed : buttonTextExpanded)));
};
CollapsibleBlock.displayName = 'CollapsibleBlock';
export default CollapsibleBlock;
